// Ported from dev-scrips-pm-react src/billing/services/use-invoice-order-lines.ts (PROD-3312 appointment Billing tab).
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import type { InvoiceServiceOrderInfo } from '@/billing/lib/pending-orders';
import { useInvoiceOrders, useRevertOrders } from '@/billing/services/use-invoice-orders';

/**
 * Order to Invoice (PROD-2592): the invoice page's view of the lines that were
 * added from pending orders.
 *
 * - `orderLines`: per `sourceOrderKey`, what the services table shows (added
 *   time, "Order removed from plan", whether the biller chose Keep).
 * - `blocking`: removed-from-plan lines still waiting for Keep or Drop. While
 *   any exist on a Draft, Issue Invoice is disabled.
 * - `keep(key)`: UI only, for this page session. Nothing is sent; the line
 *   stays and Issue unblocks. The flag comes back on reload, on purpose: it is
 *   a prompt, not a stored decision.
 * - `revert(key)`: `POST .../Orders/revert`. "Revert to pending" on a normal
 *   line, "Drop" on a removed-from-plan one. Never changes the order.
 *
 * Reads the same cached Orders query as the Pending orders section.
 */
export function useInvoiceOrderLines(invoiceId: string | undefined) {
  const { data } = useInvoiceOrders(invoiceId);
  const revertOrders = useRevertOrders();
  const [kept, setKept] = useState<ReadonlySet<string>>(new Set());

  const orderLines = useMemo(() => {
    const map: Record<string, InvoiceServiceOrderInfo & { display: string }> = {};
    for (const l of data?.added ?? []) {
      map[l.sourceOrderKey] = {
        orderType: l.orderType,
        addedOn: l.addedOn,
        removedFromPlan: l.orderRemovedFromPlan,
        kept: kept.has(l.sourceOrderKey),
        display: l.display,
      };
    }
    return map;
  }, [data, kept]);

  const blocking = Object.values(orderLines).filter((l) => l.removedFromPlan && !l.kept);

  const keep = (key: string) => setKept((prev) => new Set(prev).add(key));

  const revert = (key: string) => {
    if (!invoiceId) return;
    const line = orderLines[key];
    revertOrders.mutate(
      { invoiceId, request: { items: [{ sourceOrderKey: key }] } },
      {
        onSuccess: () => {
          if (!line) return;
          toast.success(
            line.removedFromPlan
              ? `${line.display} dropped from the invoice`
              : `${line.display} returned to pending orders`,
          );
        },
      },
    );
  };

  return { orderLines, blocking, keep, revert, busy: revertOrders.isPending };
}
